'use client';
import {
  useGetUserQuery,
  useUpdateUserMutation,
  useUpdateUserPreferencesMutation,
} from '@scaleup/frontend/api';
import { PlatformUser } from '@scaleup/models';
import { updateUserPreferences } from '../../../scaleup-platform-cdk/lib/dao/user';

import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { DateTime } from 'luxon';

type TermsStep = 'terms' | 'success';

type TermsWizardContextType = {
  user: PlatformUser;
  currentStep: TermsStep;
  setCurrentStep: (s: TermsStep) => void;
  show: boolean;
  setShow: (b: boolean) => void;
  acceptTerms: () => Promise<void>;
  isAccepting: boolean;
};

const TermsWizardContext = createContext<TermsWizardContextType>(
  {} as TermsWizardContextType
);

interface TermsWizardContextProviderProps extends PropsWithChildren {
  user: PlatformUser;
}

export const TermsWizardContextProvider: FC<
  TermsWizardContextProviderProps
> = ({ children, user }) => {
  const [currentStep, setCurrentStep] = useState<TermsStep>('terms');
  const [show, setShow] = useState(true);
  const [updateUserPreferences, { isLoading: isAccepting }] = useUpdateUserPreferencesMutation();

  const acceptTerms = async (): Promise<void> => {
    await updateUserPreferences({
        ...user.preferences,
        acceptedTermsAndConditions: true,
        acceptedTermsAndConditionsDate: DateTime.now().toUnixInteger(),
    }).unwrap();
  };

  return (
    <TermsWizardContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        show,
        setShow,
        user,
        acceptTerms,
        isAccepting,
      }}
    >
      {children}
    </TermsWizardContext.Provider>
  );
};

export const useTermsWizardContext = () =>
  useContext(TermsWizardContext);
